// Seasonal decoration modes — pure overlay, no layout impact.
// Anchor spots shared by every mode: garland under the header, accessory on the header logo,
// four corner stickers, floating particles, optional bottom strip and optional sign (top-right).

export const INTENSITIES = ['subtle', 'normal', 'festive'];

const MODES = {
  christmas: {
    label: 'Christmas', group: 'Winter holidays',
    garland: { type: 'string', rope: '#2f6b3a', items: ['🎄', '🔔', '🎁', '⭐', '🍬'], lights: ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6'] },
    accessory: 'santa',
    corners: { bl: ['🎄'], br: ['🎁', '⛄'], tl: [], tr: [] },
    particles: { kind: 'snow' },
    bottom: 'snow',
  },
  wintersale: {
    label: 'Winter Sale', group: 'Winter holidays',
    garland: { type: 'icicles' },
    accessory: '❄️',
    corners: { bl: ['⛄'], br: [], tl: ['❄️'], tr: ['❄️'] },
    particles: { kind: 'snow' },
    bottom: 'snow',
    sign: { text: 'WINTER SALE', style: 'tag', bg: '#bfe6ff', color: '#0b4f8a' },
  },
  snowday: {
    label: 'Snow Day', group: 'Winter holidays',
    garland: { type: 'string', rope: '#dbeafe', items: ['❄️', '⛄', '❄️', '🧣'], lights: ['#ffffff', '#bfdbfe'] },
    accessory: '❄️',
    corners: { bl: ['⛄'], br: ['🏔️'], tl: ['❄️'], tr: ['❄️'] },
    particles: { kind: 'snow', boost: 1.5 },
    bottom: 'snow',
  },
  newyear: {
    label: "New Year's Eve", group: 'Winter holidays',
    garland: { type: 'string', rope: '#c9a227', items: ['⭐', '🥂', '🎉', '✨'], lights: ['#fde68a', '#ffffff', '#f59e0b'] },
    accessory: 'party',
    corners: { bl: ['🎊'], br: ['🥂', '🍾'], tl: [], tr: [] },
    particles: { kind: 'confetti', colors: ['#f5c842', '#d4d4d8', '#ffffff', '#f59e0b'], fireworks: true },
    sign: { text: 'year', style: 'neon', color: '#ff5fa2' },
  },
  lunarnewyear: {
    label: 'Lunar New Year', group: 'Winter holidays',
    garland: { type: 'string', rope: '#b91c1c', items: ['🏮', '🧧', '🏮', '🪙'], lights: ['#fbbf24', '#fde68a'] },
    accessory: '🧧',
    corners: { bl: ['🏮'], br: ['🐉', '🧧'], tl: [], tr: [] },
    particles: { kind: 'glyph', glyphs: ['🪙', '✨'], motion: 'fall' },
  },
  valentines: {
    label: "Valentine's Day", group: 'Spring',
    garland: { type: 'string', rope: '#e11d48', items: ['❤️', '💗', '💝', '💗'], lights: [] },
    accessory: '🎀',
    corners: { bl: ['🌹', '💌'], br: ['🌹'], tl: [], tr: [] },
    particles: { kind: 'glyph', glyphs: ['💗', '❤️', '🩷'], motion: 'rise', size: 0.8 },
  },
  stpatricks: {
    label: "St. Patrick's Day", group: 'Spring',
    garland: { type: 'string', rope: '#166534', items: ['☘️', '🪙', '☘️', '🍀'], lights: ['#fbbf24'] },
    accessory: 'leprechaun',
    corners: { bl: ['☘️'], br: ['🌈', '🪙'], tl: ['🇮🇪'], tr: [] },
    particles: { kind: 'glyph', glyphs: ['☘️', '🍀'], motion: 'fall', size: 0.7 },
  },
  mothersday: {
    label: "Mother's Day", group: 'Spring',
    garland: { type: 'string', rope: '#f472b6', items: ['🌸', '🌷', '🌼', '💐'], lights: [] },
    accessory: '🌸',
    corners: { bl: ['💐'], br: ['🌷', '🌷'], tl: [], tr: [] },
    particles: { kind: 'petals', colors: ['#fbcfe8', '#f9a8d4', '#fde68a'] },
  },
  easter: {
    label: 'Easter', group: 'Spring',
    garland: { type: 'bunting', shape: 'egg', colors: ['#a7f3d0', '#ddd6fe', '#fed7aa', '#fef08a', '#fbcfe8'] },
    accessory: 'bunny',
    corners: { bl: ['🐣', '🌷', '🌼'], br: ['🥚', '🐰'], tl: [], tr: [] },
    particles: { kind: 'petals', colors: ['#ddd6fe', '#fbcfe8', '#fef08a', '#a7f3d0'] },
  },
  spring: {
    label: 'Spring', group: 'Spring',
    garland: { type: 'string', rope: '#4d7c0f', items: ['🌸', '🌱', '🐝', '🌸', '🦋'], lights: [] },
    accessory: '🌸',
    corners: { bl: ['🌷', '🌼'], br: ['🐝', '🌻'], tl: [], tr: [] },
    particles: { kind: 'petals', colors: ['#fbcfe8', '#ffffff', '#fce7f3'] },
  },
  earthday: {
    label: 'Earth Day / Oceans Day', group: 'Spring',
    garland: { type: 'string', rope: '#15803d', items: ['🌿', '💧', '🌍', '🐚', '🌿'], lights: [] },
    accessory: '🌍',
    corners: { bl: ['🌿', '🐚'], br: ['🐢', '🌊'], tl: [], tr: [] },
    particles: { kind: 'bubbles' },
    bottom: 'water',
  },
  summer: {
    label: 'Summer', group: 'Summer',
    garland: { type: 'bunting', shape: 'triangle', colors: ['#f87171', '#2dd4bf', '#fbbf24', '#60a5fa'] },
    accessory: '🕶️',
    corners: { bl: ['🏖️', '🩴'], br: ['🍦', '🐚'], tl: ['🌴'], tr: ['☀️'] },
    particles: { kind: 'bubbles', boost: 0.5 },
    bottom: 'water',
  },
  fathersday: {
    label: "Father's Day", group: 'Summer',
    garland: { type: 'bunting', shape: 'triangle', colors: ['#1e3a8a', '#6b7280', '#7f1d1d', '#1e3a8a'] },
    accessory: '🎩',
    corners: { bl: ['👔'], br: ['☕', '🏅'], tl: [], tr: [] },
    particles: { kind: 'confetti', colors: ['#1e3a8a', '#9ca3af', '#7f1d1d'], boost: 0.4 },
  },
  autumn: {
    label: 'Autumn', group: 'Autumn',
    garland: { type: 'string', rope: '#92400e', items: ['🍁', '🍂', '🌰', '🍁'], lights: [] },
    accessory: '🍁',
    corners: { bl: ['🍄', '🌰'], br: ['🍂', '🍁'], tl: [], tr: [] },
    particles: { kind: 'glyph', glyphs: ['🍁', '🍂'], motion: 'fall', size: 0.8 },
  },
  halloween: {
    label: 'Halloween', group: 'Autumn',
    garland: { type: 'cobweb' },
    accessory: 'witch',
    corners: { bl: ['🎃', '🎃'], br: ['🎃'], tl: [], tr: [] },
    particles: { kind: 'glyph', glyphs: ['🦇'], motion: 'drift', size: 0.8 },
  },
  blackfriday: {
    label: 'Black Friday', group: 'Sales',
    garland: { type: 'tags', tags: [{ text: 'BLACK FRIDAY', bg: '#111111', color: '#f5c842' }, { text: 'SALE', bg: '#111111', color: '#f5c842' }] },
    accessory: null,
    corners: { bl: [], br: ['🛍️'], tl: [], tr: [] },
    particles: { kind: 'confetti', colors: ['#f5c842', '#111111', '#d4d4d8'], boost: 0.4 },
  },
  flashsale: {
    label: 'Flash Sale', group: 'Sales',
    garland: { type: 'string', rope: '#ea580c', items: ['⚡', '🔥', '⚡', '💥'], lights: ['#fb923c', '#fde047'] },
    accessory: '⚡',
    corners: { bl: ['⚡'], br: ['⏱️'], tl: [], tr: [] },
    particles: { kind: 'sparks' },
    sign: { text: '⚡ FLASH SALE', style: 'tag', bg: '#ea580c', color: '#ffffff' },
  },
  bankholiday: {
    label: 'Bank Holiday Weekend', group: 'Sales',
    garland: { type: 'bunting', shape: 'triangle', colors: ['#169b62', '#ffffff', '#ff883e'] },
    accessory: null,
    corners: { bl: [], br: ['🎉'], tl: [], tr: [] },
    particles: { kind: 'confetti', colors: ['#169b62', '#ffffff', '#ff883e'], boost: 0.5 },
    sign: { text: 'BANK HOLIDAY', style: 'tag', bg: '#169b62', color: '#ffffff' },
  },
  birthday: {
    label: 'Shop Birthday / Anniversary', group: 'Sales',
    garland: { type: 'bunting', shape: 'triangle', colors: ['#f472b6', '#60a5fa', '#fbbf24', '#34d399', '#a78bfa'] },
    accessory: 'party',
    corners: { bl: ['🎈', '🎈'], br: ['🎂', '🎁'], tl: [], tr: [] },
    particles: { kind: 'confetti', colors: ['#f472b6', '#60a5fa', '#fbbf24', '#34d399', '#a78bfa'] },
  },
};

export const SEASONAL_MODES = MODES;
export const MODE_KEYS = Object.keys(MODES);
export const MODE_GROUPS = MODE_KEYS.reduce((acc, key) => {
  const g = MODES[key].group;
  (acc[g] = acc[g] || []).push({ key, label: MODES[key].label });
  return acc;
}, {});

export const getMode = (key) => (key && MODES[key]) || null;

// Same date-window rules as the store-wide banner: start inclusive, end = end of that day.
export const isSeasonActive = (settings) => {
  const key = settings?.season_mode;
  if (!key || key === 'off' || !MODES[key]) return false;
  const now = new Date();
  if (settings.season_start_date && now < new Date(settings.season_start_date)) return false;
  if (settings.season_end_date) {
    const end = new Date(settings.season_end_date);
    end.setHours(23, 59, 59, 999);
    if (now > end) return false;
  }
  return true;
};

// New Year's sign: from November onwards we celebrate the coming year.
export const celebrationYear = (d = new Date()) => d.getFullYear() + (d.getMonth() >= 10 ? 1 : 0);

// Tiny deterministic PRNG so particles don't reshuffle on every render.
export const seeded = (seed) => {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
};
